import InstallMobile from '@mui/icons-material/InstallMobile'
import IosShare from '@mui/icons-material/IosShare'
import MoreVert from '@mui/icons-material/MoreVert'
import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'preact/hooks'
import { usePromptTurn } from '../promptTurn'
import { type InstallPlatform, installPlatform } from './platform'

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISSED_KEY = 'install.dismissed'

// Fired once, possibly before the guide is mounted: kept at module level
let installPrompt: InstallPromptEvent | undefined
const subscribers = new Set<() => void>()
const notify = () => {
  for (const subscriber of subscribers) subscriber()
}
window.addEventListener('beforeinstallprompt', (event) => {
  installPrompt = event as InstallPromptEvent
  notify()
})
window.addEventListener('appinstalled', () => {
  installPrompt = undefined
  notify()
})

function currentPlatform(): InstallPlatform {
  return installPlatform({
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    standalone:
      matchMedia('(display-mode: standalone)').matches ||
      (navigator as { standalone?: boolean }).standalone === true,
  })
}

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) !== null
  } catch {
    return false
  }
}

function useInstallPrompt() {
  const [prompt, setPrompt] = useState(installPrompt)
  useEffect(() => {
    const update = () => setPrompt(installPrompt)
    subscribers.add(update)
    return () => {
      subscribers.delete(update)
    }
  }, [])
  return prompt
}

function IosSteps() {
  return (
    <>
      <Box component="ol" sx={{ pl: 2.5, my: 1 }}>
        <li>
          Touchez <IosShare fontSize="small" sx={{ verticalAlign: 'text-bottom' }} /> Partager, en
          bas de Safari (en haut sur iPad).
        </li>
        <li>Choisissez « Sur l'écran d'accueil », puis « Ajouter ».</li>
        <li>
          Ouvrez Event depuis l'écran d'accueil ; reconnectez-vous avec votre email si besoin.
        </li>
      </Box>
      <Typography variant="body2">
        Sur iPhone, seule l'application installée peut recevoir des notifications.
      </Typography>
    </>
  )
}

/** Guides the user through adding the app to their home screen, until installed or dismissed. */
export function InstallGuide() {
  const [platform, setPlatform] = useState(currentPlatform)
  const [dismissed, setDismissed] = useState(wasDismissed)
  const prompt = useInstallPrompt()
  const turn = usePromptTurn('install', platform !== 'installed' && !dismissed)

  if (!turn) return null

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, '1')
    } catch {
      // Storage blocked: the guide shows again next time
    }
    setDismissed(true)
  }
  const install = async (event: InstallPromptEvent) => {
    installPrompt = undefined
    notify()
    await event.prompt()
    if ((await event.userChoice).outcome === 'accepted') setPlatform('installed')
  }

  return (
    <Alert severity="info" icon={<InstallMobile />}>
      <AlertTitle>Installez l'application</AlertTitle>
      <Typography variant="body2">
        L'événement à portée de main, même sans réseau, et les notifications sur iPhone.
      </Typography>
      {prompt ? null : platform === 'ios' ? (
        <IosSteps />
      ) : platform === 'android' ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Ouvrez le menu <MoreVert fontSize="small" sx={{ verticalAlign: 'text-bottom' }} /> du
          navigateur, puis « Installer l'application » ou « Ajouter à l'écran d'accueil ».
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ mt: 1, overflowWrap: 'anywhere' }}>
          Sur votre téléphone, ouvrez {window.location.origin} et connectez-vous pour installer
          l'application.
        </Typography>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        {prompt && (
          <Button variant="contained" size="small" onClick={() => install(prompt)}>
            Installer
          </Button>
        )}
        <Button size="small" onClick={dismiss}>
          Plus tard
        </Button>
      </Stack>
    </Alert>
  )
}
